/**
 * ============================================================================
 * Owner-created custom pages — slug contract (the ONE source of truth)
 * ----------------------------------------------------------------------------
 * A `LocationCustomPage` (see ../locations/Location) is a standalone marketing
 * page an owner builds from the existing content-block system. It is addressed
 * by a URL-safe `slug`:
 *   - platform form   → /{restaurantSlug}/{pageSlug}
 *   - custom domain   → /{pageSlug}
 *
 * The slug MUST NOT collide with the reserved routes the renderer already owns
 * (menu / gallery / contact / story + the icon/manifest asset routes + the
 * system slugs the domain resolver rejects), and must be unique within one
 * restaurant. These pure helpers are the single contract shared by:
 *   - the Zod schema (`@order-up/schemas` RestaurantCustomPageSchema) that
 *     validates every dot-path write, and
 *   - the management Website builder, which surfaces collisions/duplicates
 *     on-screen BEFORE the write.
 * Keeping the rule here (lowest layer, no deps) means the builder and the
 * schema can never drift.
 * ============================================================================
 */

/** Max length of a custom-page slug (keeps URLs + nav labels sane). */
export const CUSTOM_PAGE_SLUG_MAX_LENGTH = 60;

/**
 * URL-safe slug shape: lowercase alphanumeric words joined by single hyphens,
 * no leading/trailing/double hyphen. Inherently rejects dots (so asset routes
 * like `favicon.ico` can never be authored) and uppercase/spaces.
 */
export const CUSTOM_PAGE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Route segments a custom-page slug may never take — the renderer resolves each
 * of these to a fixed route (or the domain resolver rejects it), so a custom
 * page with one of these slugs would be unreachable AND could shadow a real
 * route. Kept in lockstep with:
 *   - the reserved sub-pages in `apps/restaurant/src/proxy.ts`
 *     (`menu | gallery | contact | story` + the dynamic asset routes), and
 *   - `ServerDomainResolver.isSystemSlug` (robots/sitemap/_next/api/...).
 */
export const RESERVED_CUSTOM_PAGE_SLUGS: ReadonlySet<string> = new Set([
  // Fixed marketing sub-pages (app/menu, app/gallery, app/contact, app/story).
  "menu",
  "gallery",
  "contact",
  "story",
  // Guest-facing flows with their own route under `/{slug}/…`. A custom page
  // with any of these names would be shadowed by the static route and never render.
  "book",
  "catering",
  // The guest gift card balance check (spec 2026-10-08 §4.1).
  "gift-card",
  // Generated SEO page families. These are not files on disk — they are whole
  // URL spaces the platform publishes from the restaurant's own catalog
  // (`/tags/<dish|diet|daypart|service>`, `/places/<neighbourhood>`). A custom
  // page taking one of these names would silently shadow every generated page
  // under it.
  "tags",
  "places",
  // Owner preview (`/preview/<token>`), which resolves by token rather than by
  // host or slug.
  "preview",
  // Dynamic tenant asset routes served at the root.
  "favicon.ico",
  "site.webmanifest",
  "apple-touch-icon.png",
  "manifest.json",
  // System slugs the domain resolver / framework own.
  "robots.txt",
  "sitemap.xml",
  "browserconfig.xml",
  "_next",
  "api",
  "static",
  "public",
  "error",
  "not-found",
  // The internal render trees (`SiteRenderTrees.ts`). The proxy refuses both as
  // a first path segment on every host, so a custom page with either name
  // could never be reached. Literal here rather than imported so this set stays
  // a flat list the builder can read at a glance; the test pins the two equal.
  "platform-site",
  "domain-site",
  // The home path (empty slug is the home) — reserve the obvious aliases so a
  // custom page can't masquerade as the home route.
  "home",
  "index",
]);

/** Why a candidate custom-page slug is not acceptable, or `null` if it is. */
export type CustomPageSlugIssue =
  | "empty"
  | "too-long"
  | "format"
  | "reserved"
  | "duplicate";

/**
 * Coerce arbitrary owner input (e.g. a page title) into a valid slug shape:
 * lowercase, non-alphanumerics collapse to single hyphens, trimmed of edge
 * hyphens, capped at {@link CUSTOM_PAGE_SLUG_MAX_LENGTH}. May return `""` when
 * the input has no usable characters — callers must treat that as "not yet a
 * valid slug" (see {@link validateCustomPageSlug}).
 */
export function normalizeCustomPageSlug(input: string): string {
  // Hyphen trimming is a linear scan, not `/^-+|-+$/g`: an anchored
  // quantifier backtracks and is polynomial in the input
  // (CodeQL js/polynomial-redos). `packages/models` sits below
  // `packages/common` in the dependency flow, so the helper there is not
  // importable here and this is inlined deliberately.
  const trimHyphens = (v: string): string => {
    let a = 0;
    let b = v.length;
    while (a < b && v[a] === "-") a += 1;
    while (b > a && v[b - 1] === "-") b -= 1;
    return v.slice(a, b);
  };
  const collapsed = (input ?? "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-");
  return trimHyphens(trimHyphens(collapsed).slice(0, CUSTOM_PAGE_SLUG_MAX_LENGTH));
}

/** True when `slug` collides with a reserved route (case-insensitive). */
export function isReservedCustomPageSlug(slug: string): boolean {
  return RESERVED_CUSTOM_PAGE_SLUGS.has((slug ?? "").toLowerCase());
}

/**
 * Validate a single slug's shape + reserved-collision (NOT uniqueness — that is
 * cross-page, see {@link findDuplicateCustomPageSlugs}). Returns the first issue
 * found, or `null` when the slug is acceptable in isolation.
 */
export function validateCustomPageSlug(
  slug: string,
): Exclude<CustomPageSlugIssue, "duplicate"> | null {
  const value = slug ?? "";
  if (!value.trim()) return "empty";
  if (value.length > CUSTOM_PAGE_SLUG_MAX_LENGTH) return "too-long";
  if (!CUSTOM_PAGE_SLUG_PATTERN.test(value)) return "format";
  if (isReservedCustomPageSlug(value)) return "reserved";
  return null;
}

/**
 * Return the set of slugs that appear more than once (case-insensitively) in the
 * given list — the uniqueness half of the contract. Empty when all are unique.
 */
export function findDuplicateCustomPageSlugs(
  slugs: readonly string[],
): ReadonlySet<string> {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const raw of slugs) {
    const slug = (raw ?? "").toLowerCase();
    if (!slug) continue;
    if (seen.has(slug)) dupes.add(slug);
    else seen.add(slug);
  }
  return dupes;
}

/** Human-facing copy for a slug issue, for the builder's on-screen notices. */
export function customPageSlugIssueMessage(
  issue: CustomPageSlugIssue,
  slug?: string,
): string {
  const label = slug ? `"${slug}"` : "This page URL";
  switch (issue) {
    case "empty":
      return "Enter a page URL (letters, numbers, and hyphens).";
    case "too-long":
      return `${label} is too long — use ${CUSTOM_PAGE_SLUG_MAX_LENGTH} characters or fewer.`;
    case "format":
      return `${label} isn't a valid URL. Use lowercase letters, numbers, and single hyphens (e.g. "private-events").`;
    case "reserved":
      return `${label} is a reserved page name. Pick a different URL (menu, gallery, contact, and story are built in).`;
    case "duplicate":
      return `${label} is already used by another page. Each page needs a unique URL.`;
    default:
      return `${label} isn't a valid page URL.`;
  }
}
